import { SessionContext, SessionContextType } from '@contexts/session-context';
import { Session } from '@models/session.model';
import { Subscription } from '@models/subscription.model';
import { SubscriptionService } from '@services/subscription.service';
import React, { useEffect, useRef, useState } from 'react';
import SessionService from '../services/session.service';
import { newRelicUtils } from '@utils/newrelic';

export default function SessionContextProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [session, setSession] = useState<Session | undefined>(undefined);
  const [isSessionLoading, setIsSessionLoading] = useState<boolean>(false);

  const create = (sessionValue: Session, callback: VoidFunction) => {
    setSession(sessionValue);
    callback();
  };

  const destroy = (callback: VoidFunction) => {
    setSession(undefined);
    callback();
  };

  const sessionRef = useRef(session); // Use a ref to store the latest session value

  const fetchSubscription = async (
    sessionInfo: Session,
    isLoadingFalse?: boolean,
  ) => {
    const { tenant } = sessionInfo;
    if (isLoadingFalse) setIsSessionLoading(true);

    try {
      const res: Subscription = await SubscriptionService.show(
        tenant.subscription.id,
      );
      if (isLoadingFalse) setIsSessionLoading(false);

      setSession({
        ...sessionInfo,
        tenant: {
          ...sessionInfo.tenant,
          subscription: res,
        },
      });
    } catch (error) {
      if (isLoadingFalse) setIsSessionLoading(false);
      // Handle the error as needed
      console.error('Error fetching subscription:', error);
    }
  };

  const fetchSession = () => {
    setIsSessionLoading(false);
    SessionService.show().then(
      (res) => {
        if (res instanceof Session && res.user) {
          setSession(res);
          fetchSubscription(res, true);
        } else {
          window.location.href = `${
            import.meta.env.VITE_ID_SERVICE_URL
          }logout?redirectTo=${window.location.href}`;
        }
      },
      (res) => {
        if (res?.response?.status === 503)
          window.location.href = `${window.location.origin}/ui/under_maintenance`;
        else
          window.location.href = `${
            import.meta.env.VITE_ID_SERVICE_URL
          }logout?redirectTo=${window.location.href}`;
      },
    );
  };

  const refreshSession = () => {
    setSession(session);
    fetchSession();
  };

  useEffect(() => {
    // Update the sessionRef when session changes
    sessionRef.current = session;
    if (newRelicUtils.isInitialized() && session?.tenant?.tsid) {
      newRelicUtils.getNewRelic()?.setCustomAttribute("tsid", session?.tenant?.tsid);
    }
  }, [session]);

  useEffect(() => {
    const fetchData = async () => {
      const currentSession = sessionRef.current; // Use the latest session value
      if (currentSession) {
        // Initial call
        await fetchSubscription(currentSession);
      }
    };

    fetchData();

    const intervalId = setInterval(fetchData, 60 * 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    fetchSession();
  }, []);

  return (
    <>
      {session && (
        <SessionContext.Provider
          value={
            {
              session,
              create,
              destroy,
              refreshSession,
              isSessionLoading,
            } as SessionContextType
          }
        >
          {children}
        </SessionContext.Provider>
      )}
    </>
  );
}
